'use client';

import s from 'BomdisoftComponents/painted-week/styles.module.css';
import { PAINTED_WEEKDAYS, type PaintedCells, type PaintedWeekday, type PaintedWindow, paintedCells, runsOf, SLOTS, timeOf, windowsOf, withRectangle } from 'BomdisoftComponents/painted-week/windows';
import { type KeyboardEvent, type PointerEvent, type ReactNode, useId, useMemo, useRef, useState } from 'react';

export { PAINTED_WEEKDAYS, type PaintedCells, type PaintedRun, type PaintedWeekday, type PaintedWindow, paintedCells, runsOf, SLOTS, slotOf, timeOf, windowsOf, withRectangle } from 'BomdisoftComponents/painted-week/windows';

/** The words the week is read by: the component carries none of its own, so a
 *  product reads it in its reader's language. */
export type PaintedWeekLabels = {
  /** A cell's own name, for the focus and the pointer: "Monday, 09:00". */
  cell: (day: PaintedWeekday, time: string) => string;
  days: Record<PaintedWeekday, string>;
  /** The scale's tick, given `HH:MM`; identity when the reader's clock is 24h. */
  hour: (time: string) => string;
  /** The line under the grid saying what painted means. */
  legend: string;
  /** A painted run's name, read out for each: "Monday, 09:00 to 17:30". */
  span: (day: PaintedWeekday, open: string, close: string) => string;
};

export type PaintedWeekProps = {
  labels: PaintedWeekLabels;
  /** The windows after a stroke, one per painted run, each naming one day. */
  onChange: (windows: PaintedWindow[]) => void;
  testId?: string;
  value: PaintedWindow[];
};

/** A scale label every three hours (user pick D, with the corrections given at
 *  the pick: every day labelled, a visible grid, a heavier line on the hour). */
const LABEL_EVERY_SLOTS = 6;

type Cell = { day: PaintedWeekday; slot: number };

const cellAt = (element: Element | null): Cell | null => {
  const target = element?.closest('[data-day][data-slot]');
  if (!target) return null;
  const day = target.getAttribute('data-day') as PaintedWeekday | null;
  const slot = Number(target.getAttribute('data-slot'));
  return day && Number.isInteger(slot) ? { day, slot } : null;
};

/**
 * A week of half-hour cells the hours are painted onto: the picture is the
 * schedule. A pointer stroke paints or clears the rectangle it covers, with
 * the first cell deciding which; arrows move a cell at a time, space toggles
 * one, shift with an arrow carries the cell's state to the next. Touch is a
 * pointer. Each painted run is named for a reader with no sight.
 */
export const PaintedWeek = ({ labels, onChange, testId, value }: PaintedWeekProps): ReactNode => {
  const cells = useMemo(() => paintedCells(value), [value]);
  const [focused, setFocused] = useState<Cell>({ day: 'mon', slot: 18 });
  const stroke = useRef<{ base: PaintedCells; from: Cell; painted: boolean } | null>(null);
  const spansId = useId();

  const commit = (next: PaintedCells) => onChange(windowsOf(next));

  const beginStroke = (event: PointerEvent<HTMLElement>) => {
    const cell = cellAt(event.target as Element);
    if (!cell) return;
    event.preventDefault();
    const painted = !cells[cell.day][cell.slot];
    stroke.current = { base: cells, from: cell, painted };
    setFocused(cell);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    commit(withRectangle(cells, cell, cell, painted));
  };

  const extendStroke = (event: PointerEvent<HTMLElement>) => {
    const held = stroke.current;
    if (!held) return;
    const under = typeof document === 'undefined' ? null : document.elementFromPoint(event.clientX, event.clientY);
    const cell = cellAt(under);
    if (!cell) return;
    commit(withRectangle(held.base, held.from, cell, held.painted));
  };

  const endStroke = (event: PointerEvent<HTMLElement>) => {
    if (!stroke.current) return;
    stroke.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const move = (event: KeyboardEvent<HTMLButtonElement>, cell: Cell) => {
    const dayIndex = PAINTED_WEEKDAYS.indexOf(cell.day);
    const steps: Record<string, Cell | undefined> = {
      ArrowDown: dayIndex < PAINTED_WEEKDAYS.length - 1 ? { day: PAINTED_WEEKDAYS[dayIndex + 1] as PaintedWeekday, slot: cell.slot } : undefined,
      ArrowLeft: cell.slot > 0 ? { day: cell.day, slot: cell.slot - 1 } : undefined,
      ArrowRight: cell.slot < SLOTS - 1 ? { day: cell.day, slot: cell.slot + 1 } : undefined,
      ArrowUp: dayIndex > 0 ? { day: PAINTED_WEEKDAYS[dayIndex - 1] as PaintedWeekday, slot: cell.slot } : undefined,
    };
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      commit(withRectangle(cells, cell, cell, !cells[cell.day][cell.slot]));
      return;
    }
    const next = steps[event.key];
    if (!next) return;
    event.preventDefault();
    setFocused(next);
    if (event.shiftKey) commit(withRectangle(cells, next, next, cells[cell.day][cell.slot]));
    const root = event.currentTarget.closest('[data-painted-week]');
    const target = root?.querySelector<HTMLButtonElement>(`[data-day="${next.day}"][data-slot="${next.slot}"]`);
    target?.focus();
  };

  const spans = PAINTED_WEEKDAYS.flatMap(day => runsOf(cells[day]).map(run => labels.span(day, timeOf(run.start), timeOf(run.end))));

  return (
    <div data-painted-week='' data-testid={testId}>
      {/* No grid roles: every cell is a toggle button carrying its own name,
          which is the whole of what a reader with no sight needs, and the
          scale is decoration beside those names. */}
      {/* The frame scrolls the week sideways once the cells reach their floor
          (a phone), the way a data grid scrolls its columns; the page never
          scrolls sideways for it (user, 2026-09-20). */}
      <div className={s.frame} data-testid={testId ? `${testId}-frame` : undefined}>
        <div aria-describedby={spansId} className={s.week} data-testid={testId ? `${testId}-grid` : undefined} onPointerCancel={endStroke} onPointerDown={beginStroke} onPointerMove={extendStroke} onPointerUp={endStroke}>
          <div className={s.row}>
            <span className={s.corner} />
            {Array.from({ length: SLOTS }, (_, slot) => (
              <span aria-hidden='true' className={s.tick} data-hour={slot % 2 === 0 ? '' : undefined} key={slot}>
                {slot % LABEL_EVERY_SLOTS === 0 ? labels.hour(timeOf(slot)) : ''}
              </span>
            ))}
          </div>
          {PAINTED_WEEKDAYS.map(day => {
            const row = cells[day];
            return (
              <div className={s.row} data-testid={testId ? `${testId}-day-${day}` : undefined} key={day}>
                <span aria-hidden='true' className={s.dayLabel}>
                  {labels.days[day]}
                </span>
                {row.map((painted, slot) => {
                  const start = painted && (slot === 0 || !row[slot - 1]);
                  const end = painted && (slot === SLOTS - 1 || !row[slot + 1]);
                  const isFocused = focused.day === day && focused.slot === slot;
                  return (
                    <button
                      aria-label={labels.cell(day, timeOf(slot))}
                      aria-pressed={painted}
                      className={s.cell}
                      data-day={day}
                      data-end={end ? '' : undefined}
                      data-hour={slot % 2 === 0 ? '' : undefined}
                      data-slot={slot}
                      data-start={start ? '' : undefined}
                      data-testid={testId ? `${testId}-cell-${day}-${slot}` : undefined}
                      key={slot}
                      onFocus={() => setFocused({ day, slot })}
                      onKeyDown={event => move(event, { day, slot })}
                      tabIndex={isFocused ? 0 : -1}
                      type='button'
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      <p className={s.legend}>
        <span className={s.swatch} />
        {labels.legend}
      </p>
      <ul className={s.spans} data-testid={testId ? `${testId}-spans` : undefined} id={spansId}>
        {spans.map(name => (
          <li key={name}>{name}</li>
        ))}
      </ul>
    </div>
  );
};
